import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getBrandPlan, isBrandFirstPlan } from '../utils/brandIdentity';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config';
import { getBearerAuthHeaders } from '../utils/authFetchHeaders';
import {
  getActiveEntitySlug,
  setActiveEntitySlug,
  setActiveEntityBrand,
  clearActiveEntity,
  ENTITY_CHANGED_EVENT,
} from '../utils/activeEntity';
import './EntitySwitcher.css';

/**
 * Entity (brand) picker for the sidebar — "Vector Algorithmics" / "Kairos".
 *
 * Sits directly under the RiskLo mark, above the Results section, showing the
 * active entity's logo and name.
 *
 * Visibility rules:
 *   • belongs to ONE entity  → label; its menu only offers "Refresh my brand access"
 *   • belongs to 2+ entities → clickable picker with a dropdown (+ refresh)
 *   • full admin             → sees every entity (server decides, not this file)
 *
 * "Refresh my brand access" re-checks Whop right away for this user, for
 * someone who just bought a brand and is still seeing a cached "not a member".
 *
 * The chosen slug is only a PREFERENCE. It rides along as X-RiskLo-Entity and
 * the server validates it against the entities the caller provably belongs to,
 * so tampering can switch brands you already have but never grant a new one.
 */
export default function EntitySwitcher({ collapsed = false }) {
  const { user, loading: authLoading, isSelfUsePreview } = useAuth();
  const [entities, setEntities] = useState([]);
  const [activeSlug, setActiveSlug] = useState(() => getActiveEntitySlug());
  const [open, setOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshNote, setRefreshNote] = useState(null);
  const wrapRef = useRef(null);

  const applyMe = useCallback((data) => {
      setEntities(Array.isArray(data.entities) ? data.entities : []);
      // The server has the final say — adopt its active entity so a stale
      // localStorage slug (access revoked, entity deactivated, a different user
      // signing in on this browser) self-corrects.
      if (data.activeEntity?.slug) {
        setActiveSlug(data.activeEntity.slug);
        // Cache branding so the sidebar can label "<Brand> Results" without
        // every consumer refetching.
        setActiveEntityBrand(data.activeEntity);
        if (getActiveEntitySlug() !== data.activeEntity.slug) {
          setActiveEntitySlug(data.activeEntity.slug);
        }
      } else {
        // No active entity means this caller belongs to none. Anything left in
        // storage is someone else's brand, and keeping it would label the app
        // with a brand the caller cannot reach.
        setActiveSlug(null);
        clearActiveEntity();
      }
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/entities/me`, {
        headers: getBearerAuthHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) return;
      applyMe(data);
    } catch (_err) {
      /* switcher stays hidden */
    }
  }, [applyMe]);

  const refreshAccess = async () => {
    if (refreshing) return;
    setRefreshing(true);
    setRefreshNote(null);
    const before = new Set(entities.map((e) => e.slug));
    try {
      const res = await fetch(`${API_BASE_URL}/api/entities/me/refresh-access`, {
        method: 'POST',
        headers: getBearerAuthHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        setRefreshNote({ tone: 'warn', text: data.error || 'Could not refresh right now. Try again shortly.' });
        return;
      }
      applyMe(data);
      const added = (data.entities || []).filter((e) => !before.has(e.slug)).map((e) => e.displayName);
      setRefreshNote(added.length
        ? { tone: 'good', text: `Added: ${added.join(', ')}` }
        : { tone: 'muted', text: 'Up to date. No new brands found. If you just bought one, give Whop a minute and try again.' });
    } catch (_err) {
      setRefreshNote({ tone: 'warn', text: 'Could not refresh right now. Try again shortly.' });
    } finally {
      setRefreshing(false);
    }
  };

  // Load once auth is ready. On mount-only effects, OAuth/Clerk sign-in on
  // dev/staging often fires /api/entities/me before the JWT is persisted, so
  // the switcher stayed hidden until a full page refresh.
  useEffect(() => {
    if (authLoading) return undefined;
    if (!user?.email) {
      setEntities([]);
      setActiveSlug(null);
      setOpen(false);
      return undefined;
    }
    load();
    return undefined;
  }, [authLoading, user?.email, load]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const choose = (slug) => {
    setOpen(false);
    if (!slug || slug === activeSlug) return;
    setActiveSlug(slug);
    setActiveEntityBrand(entities.find((e) => e.slug === slug) || null);
    // Broadcasts ENTITY_CHANGED_EVENT; App listens and refetches entity-scoped
    // data (strategy list, dashboards) so the whole app follows the new brand.
    setActiveEntitySlug(slug);
  };

  const active = entities.find((e) => e.slug === activeSlug) || entities[0];
  const multi = entities.length > 1;

  // Previewing as a self user (no company): there is no brand to show or switch.
  if (isSelfUsePreview) return null;

  // One brand. White-label: the sidebar header already is the company, so nothing
  // here. Starter / Brand: RiskLo is the app and this shows the company's workspace
  // ("Vector · on RiskLo"), with no menu (nothing to switch to).
  if (!multi) {
    if (!active || active.slug === 'risklo' || isBrandFirstPlan(getBrandPlan(active))) return null;
    const logo = active.branding?.logoUrl;
    const mark = logo
      ? <img src={logo} alt="" aria-hidden="true" className="entity-switcher-logo" />
      : (
        <span
          className="entity-switcher-logo entity-switcher-logo--fallback"
          style={{ background: active.branding?.accentColor || 'rgba(129, 140, 248, 0.25)' }}
          aria-hidden="true"
        >
          {(active.displayName || '?').trim().charAt(0).toUpperCase()}
        </span>
      );
    return (
      <div className={`entity-switcher entity-switcher--workspace${collapsed ? ' entity-switcher--collapsed' : ''}`} title={`${active.displayName} workspace on RiskLo`}>
        <div className={collapsed ? 'entity-switcher-rail' : 'entity-switcher-trigger is-single'}>
          {mark}
          {collapsed ? null : (
            <span className="entity-switcher-text">
              <span className="entity-switcher-name">{active.displayName}</span>
              <span className="entity-switcher-hint">on RiskLo</span>
            </span>
          )}
        </div>
      </div>
    );
  }
  const logoFor = (e) => e?.branding?.logoUrl || null;

  const Mark = ({ entity }) => {
    const url = logoFor(entity);
    if (url) return <img src={url} alt="" aria-hidden="true" className="entity-switcher-logo" />;
    // No logo uploaded yet: initial-letter tile in the entity's accent color.
    return (
      <span
        className="entity-switcher-logo entity-switcher-logo--fallback"
        style={{ background: entity?.branding?.accentColor || 'rgba(129, 140, 248, 0.25)' }}
        aria-hidden="true"
      >
        {(entity?.displayName || '?').trim().charAt(0).toUpperCase()}
      </span>
    );
  };

  const refreshFooter = (
    <li className="entity-switcher-refresh" role="none">
      <button
        type="button"
        className="entity-switcher-refresh-btn"
        onClick={refreshAccess}
        disabled={refreshing}
      >
        <span aria-hidden="true" className={`entity-switcher-refresh-icon${refreshing ? ' is-spinning' : ''}`}>↻</span>
        <span>{refreshing ? 'Checking Whop…' : 'Refresh my brand access'}</span>
      </button>
      {refreshNote ? (
        <p className={`entity-switcher-refresh-note is-${refreshNote.tone}`} role="status">{refreshNote.text}</p>
      ) : (
        <p className="entity-switcher-refresh-note is-muted">Just bought a brand on Whop? Refresh to see it now.</p>
      )}
    </li>
  );

  if (collapsed) {
    // Rail mode: mark only, still switchable when there is more than one.
    return (
      <div className="entity-switcher entity-switcher--collapsed" ref={wrapRef}>
        <button
          type="button"
          className="entity-switcher-rail"
          onClick={() => setOpen((v) => !v)}
          title={multi ? `${active?.displayName} — switch brand` : `${active?.displayName} — refresh brand access`}
          aria-haspopup="menu"
          aria-expanded={open}
        >
          <Mark entity={active} />
        </button>
        {open ? (
          <ul className="entity-switcher-menu entity-switcher-menu--rail" role="listbox">
            {multi && entities.map((e) => (
              <li key={e.slug}>
                <button
                  type="button"
                  role="option"
                  aria-selected={e.slug === active?.slug}
                  className={`entity-switcher-item ${e.slug === active?.slug ? 'active' : ''}`}
                  onClick={() => choose(e.slug)}
                >
                  <Mark entity={e} />
                  <span>{e.displayName}</span>
                </button>
              </li>
            ))}
            {refreshFooter}
          </ul>
        ) : null}
      </div>
    );
  }

  return (
    <div className="entity-switcher" ref={wrapRef}>
      <button
        type="button"
        className={`entity-switcher-trigger ${multi ? '' : 'is-single'}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={multi ? 'Switch brand' : `${active?.displayName} — refresh brand access`}
      >
        <Mark entity={active} />
        <span className="entity-switcher-text">
          <span className="entity-switcher-name">{active?.displayName}</span>
          <span className="entity-switcher-hint">{multi ? 'Switch brand' : 'Workspace'}</span>
        </span>
        <span className="entity-switcher-caret" aria-hidden="true">▾</span>
      </button>

      {open ? (
        <ul className="entity-switcher-menu" role="listbox">
          {multi && entities.map((e) => (
            <li key={e.slug}>
              <button
                type="button"
                role="option"
                aria-selected={e.slug === active?.slug}
                className={`entity-switcher-item ${e.slug === active?.slug ? 'active' : ''}`}
                onClick={() => choose(e.slug)}
              >
                <Mark entity={e} />
                <span>{e.displayName}</span>
              </button>
            </li>
          ))}
          {refreshFooter}
        </ul>
      ) : null}
    </div>
  );
}

export { ENTITY_CHANGED_EVENT };
